export const CATALOG_LEVELS = ["beginner", "intermediate", "advanced"] as const;
export type CatalogLevel = (typeof CATALOG_LEVELS)[number];

export const CATALOG_CATEGORIES = [
  { id: "viagem", label: "Viagem", emoji: "✈️" },
  { id: "comida", label: "Comida", emoji: "🍽️" },
  { id: "trabalho", label: "Trabalho", emoji: "💼" },
  { id: "saude", label: "Saúde", emoji: "🩺" },
  { id: "compras", label: "Compras", emoji: "🛍️" },
  { id: "social", label: "Social", emoji: "👋" },
  { id: "casa", label: "Casa", emoji: "🏠" },
  { id: "estudo", label: "Estudo", emoji: "📚" },
  { id: "transporte", label: "Transporte", emoji: "🚇" },
  { id: "emergencia", label: "Emergência", emoji: "🚨" },
  { id: "banco", label: "Banco", emoji: "🏦" },
  { id: "entretenimento", label: "Lazer", emoji: "🎬" },
] as const;

export type CatalogCategoryId = (typeof CATALOG_CATEGORIES)[number]["id"];

export type CatalogSituation = {
  slug: string;
  title: string;
  subtitle: string;
  level: CatalogLevel;
  category: CatalogCategoryId;
  aiRole: string;
  studentGoal: string;
  promptNotes: string;
  estimatedMinutes: number;
};

export const LEVEL_LABELS: Record<CatalogLevel, string> = {
  beginner: "Começando",
  intermediate: "Intermediário",
  advanced: "Avançado",
};

export const LEVEL_HINTS: Record<CatalogLevel, string> = {
  beginner:
    "Speak slowly and clearly. Use A1-A2 English only. Short sentences. One question at a time. Wait patiently. No idioms.",
  intermediate:
    "Use natural B1 everyday English. Add one small unexpected question or complication. Keep turns to 2-3 sentences.",
  advanced:
    "Use native pace and B2-C1 English. Include nuance, negotiation, or mild conflict. Do not oversimplify.",
};

export function isCatalogLevel(value: string | null | undefined): value is CatalogLevel {
  return CATALOG_LEVELS.includes(value as CatalogLevel);
}

export function isCatalogCategoryId(value: string | null | undefined): value is CatalogCategoryId {
  return CATALOG_CATEGORIES.some((category) => category.id === value);
}

export function getCategory(id: CatalogCategoryId) {
  return CATALOG_CATEGORIES.find((category) => category.id === id)!;
}

export function filterCatalog(
  items: CatalogSituation[],
  level: CatalogLevel | "all",
  category: CatalogCategoryId | "all",
): CatalogSituation[] {
  return items.filter((item) => {
    if (level !== "all" && item.level !== level) return false;
    if (category !== "all" && item.category !== category) return false;
    return true;
  });
}

export function findCatalogSituation(
  items: CatalogSituation[],
  slug: string | undefined,
): CatalogSituation | undefined {
  if (!slug) return undefined;
  return items.find((item) => item.slug === slug);
}

export function pickNextSlug(
  items: CatalogSituation[],
  current: CatalogSituation,
): string {
  const sameLevel = items.filter(
    (item) => item.level === current.level && item.slug !== current.slug,
  );
  const sameCategory = sameLevel.filter((item) => item.category === current.category);
  const pool = sameCategory.length ? sameCategory : sameLevel;
  if (!pool.length) return current.slug;
  return pool[Math.floor(Math.random() * pool.length)].slug;
}

/** Seed id embutido no slug: `viagem-checkin-aeroporto-beginner` → `checkin-aeroporto`. */
export function catalogSeedId(situation: Pick<CatalogSituation, "slug" | "category" | "level">): string {
  const prefix = `${situation.category}-`;
  const suffix = `-${situation.level}`;
  if (!situation.slug.startsWith(prefix) || !situation.slug.endsWith(suffix)) {
    return situation.slug;
  }
  return situation.slug.slice(prefix.length, situation.slug.length - suffix.length);
}

const NEXT_RHYTHM: Record<CatalogLevel, CatalogLevel | undefined> = {
  beginner: "intermediate",
  intermediate: "advanced",
  advanced: undefined,
};

/** Próximo ritmo do mesmo seed. Sem sorteio de categoria. */
export function pickNextRhythm(
  items: CatalogSituation[],
  current: CatalogSituation,
): string | undefined {
  const nextLevel = NEXT_RHYTHM[current.level];
  if (!nextLevel) return undefined;
  const seedId = catalogSeedId(current);
  return items.find(
    (item) =>
      item.category === current.category &&
      item.level === nextLevel &&
      catalogSeedId(item) === seedId,
  )?.slug;
}
